/**
 * Terms & Conditions Acceptance Tracking Utility
 * 
 * Captures comprehensive data for legal compliance when users accept T&C.
 * Stores: timestamp, IP, geolocation, device fingerprint, user agent, etc.
 */

export interface TermsAcceptanceRecord {
    timestamp: string; // ISO 8601 UTC
    ip_address: string;
    ip_location: {
        country?: string;
        region?: string;
        city?: string;
        timezone?: string;
    };
    user_agent: string;
    device_fingerprint: string;
    terms_version: string; // Date-based: "2025-12-04"
    acceptance_type: 'registration' | 'email_verification' | 'terms_update';
    url: string;
    browser_info: {
        language: string;
        platform: string;
        screen_resolution: string;
        color_depth: number;
        timezone_offset: number;
    };
}

/**
 * Generate a browser fingerprint (simple hash-based approach)
 */
function generateFingerprint(): string {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    let fingerprint = '';

    // Canvas fingerprinting
    if (ctx) {
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillText('0DTE-Toolbox', 2, 2);
        fingerprint += canvas.toDataURL();
    }

    // Combine various browser properties
    const components = [
        navigator.userAgent,
        navigator.language,
        screen.colorDepth,
        screen.width + 'x' + screen.height,
        new Date().getTimezoneOffset(),
        !!window.sessionStorage,
        !!window.localStorage,
    ].join('|');

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < components.length; i++) {
        const char = components.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }

    return Math.abs(hash).toString(16);
}

/**
 * Fetch IP geolocation data
 */
async function getIpLocation(ip: string): Promise<TermsAcceptanceRecord['ip_location']> {
    try {
        // Using ipapi.co free tier (1000 requests/day)
        const response = await fetch(`https://ipapi.co/${ip}/json/`);
        const data = await response.json();

        return {
            country: data.country_name,
            region: data.region,
            city: data.city,
            timezone: data.timezone,
        };
    } catch (error) {
        console.error('Failed to fetch IP location:', error);
        return {};
    }
}

/**
 * Get client IP address via API route
 */
async function getClientIp(): Promise<string> {
    try {
        const response = await fetch('/api/get-client-ip');
        const data = await response.json();
        return data.ip || 'unknown';
    } catch (error) {
        console.error('Failed to get client IP:', error);
        return 'unknown';
    }
}

/**
 * Capture comprehensive T&C acceptance data
 */
export async function captureTermsAcceptance(
    acceptanceType: TermsAcceptanceRecord['acceptance_type']
): Promise<TermsAcceptanceRecord> {
    const ip = await getClientIp();
    const ipLocation = await getIpLocation(ip);

    const record: TermsAcceptanceRecord = {
        timestamp: new Date().toISOString(),
        ip_address: ip,
        ip_location: ipLocation,
        user_agent: navigator.userAgent,
        device_fingerprint: generateFingerprint(),
        terms_version: '2025-12-04', // Update this when T&C changes
        acceptance_type: acceptanceType,
        url: window.location.href,
        browser_info: {
            language: navigator.language,
            platform: navigator.platform,
            screen_resolution: `${screen.width}x${screen.height}`,
            color_depth: screen.colorDepth,
            timezone_offset: new Date().getTimezoneOffset(),
        },
    };

    return record;
}

/**
 * Add a new acceptance record to existing array
 */
export function addAcceptanceRecord(
    existingRecords: TermsAcceptanceRecord[] | null | undefined,
    newRecord: TermsAcceptanceRecord
): TermsAcceptanceRecord[] {
    const records = Array.isArray(existingRecords) ? existingRecords : [];
    return [...records, newRecord];
}
